import { FC, ReactElement, useEffect, useState } from 'react';
import { CloseButton, Form } from 'react-bootstrap';
import { Search } from 'react-bootstrap-icons';
import i18next from 'i18next';

type SearchBarProps = {
  onSearch: (value: string) => void;
  placeholder?: string;
  debounceMs?: number;
  maxWidth?: number;
};

const SearchBar: FC<SearchBarProps> = ({
  onSearch,
  placeholder = i18next.t('generic.search'),
  debounceMs = 300,
  maxWidth,
}: SearchBarProps): ReactElement => {
  const [inputValue, setInputValue] = useState('');
  const [debouncedValue, setDebouncedValue] = useState('');

  useEffect(() => {
    const handler: NodeJS.Timeout = setTimeout(() => setDebouncedValue(inputValue), debounceMs);
    return (): void => clearTimeout(handler);
  }, [inputValue, debounceMs]);

  useEffect(() => {
    onSearch(debouncedValue.trim());
  }, [debouncedValue, onSearch]);

  const clearSearch = (): void => {
    setInputValue('');
    setDebouncedValue('');
    onSearch('');
  };
  
  const wrapperStyle: React.CSSProperties | undefined = maxWidth !== undefined ? { maxWidth } : undefined;

  return (
    <div className={`position-relative ${maxWidth === undefined ? 'w-100' : ''}`} style={wrapperStyle}>
      <Search
        size={18}
        className="text-muted position-absolute top-50 translate-middle-y"  
        style={{ left: '0.75rem', pointerEvents: 'none' }}
        aria-hidden 
      />

      <Form.Control
        type="search"
        placeholder={placeholder}
        value={inputValue}
        onChange={(event) => setInputValue(event.target.value)}
        className="ps-5 pe-5"
        style={{ WebkitAppearance: 'none' }}
        onFocus={(event) => event.currentTarget.style.boxShadow = '0 0 0 0.25rem rgba(var(--bs-primary-rgb), 0.25)'}
        onBlur={(event) => event.currentTarget.style.boxShadow = ''}
      />

      {inputValue && (
        <CloseButton
          aria-label={i18next.t('generic.clearSearch')}
          onClick={clearSearch}
          className="position-absolute top-50 translate-middle-y opacity-50"
          style={{
            right: '0.75rem',
            width: '1.25rem',
            height: '1.25rem',
            padding: 0
          }}
        />
      )}
    </div>
  );
};

export default SearchBar;
